import { cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { upperCase } from "lodash-es"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { SplitButtonContainer } from "@/components/Button/SplitButton.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { PrStatus } from "@/components/GitHubIntegration/PrStatus.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { ChevronDownLight, GithubFill, Icon16, Icon20 } from "@/icons/Icons.ts"
import { pluralize } from "@/lib/displayUtils.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"
import { CreatePrDialog } from "@/routes/tasks/TaskViewScene/CreatePrDialog.tsx"
import { type TaskViewModel, versionTitle } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import { Rotate0, Rotate180 } from "@/styles/mixins.ts"


export const GitHubDropdown = observer((props: {
  model: TaskViewModel
}) => {

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [prDialogOpen, setPrDialogOpen] = useState(false)

  const task = props.model.task.get()

  const me = props.model.me.get()

  const openedPrs = props.model.gitHubModel.gitHubPrs.get()
  const latestPr = openedPrs.length > 0 ? openedPrs[openedPrs.length - 1] : null

  const hasChanges = props.model.changesModel.files.get().length > 0
  const hasNewChanges = hasChanges && !openedPrs.some(pr => pr.taskVersion == props.model.historyItems.get().length)

  const isConnected = props.model.gitHubModel.gitHubStatus.get()?.isConnected

  const [tooltipOpen, setTooltipOpen] = useState(false)

  return (
    <>
      {isConnected == false && (
        <GitHubButton kind="alternative" size="large" onClick={props.model.gitHubModel.connectToGitHub}>
          <Horizontal alignItems="center" gap="8px">
            <GithubFill className={Icon20} />
            Configure GitHub
          </Horizontal>
        </GitHubButton>
      )}

      {isConnected == true && (
        <>
          {!latestPr && (
            <Tooltip
              open={!hasNewChanges && tooltipOpen}
              onOpenChange={setTooltipOpen}
              content="You can create a pull request after a new version becomes available"
            >
              <GitHubButton kind="alternative" disabled={!hasNewChanges} size="large" onClick={(e) => {
                e.stopPropagation()
                props.model.gitHubModel.trackCreatePullRequestClick()
                setPrDialogOpen(true)
              }}>
                <Horizontal alignItems="center" gap="8px">
                  <GithubFill className={Icon20} />
                  Create pull request
                </Horizontal>
              </GitHubButton>
            </Tooltip>
          )}

          {latestPr && openedPrs.length == 1 && !hasNewChanges && (
            <GitHubButton kind="alternative" size="large" onClick={(e => {
              e.stopPropagation()
              openLinkInNewWindow(latestPr.url)
            })}>
              <Horizontal alignItems="center" gap="8px">
                <GithubFill className={Icon20} />
                #{latestPr.number}
                <PrStatus kind={latestPr.state}>{upperCase(latestPr.state)}</PrStatus>
              </Horizontal>
            </GitHubButton>
          )}

          {latestPr && (openedPrs.length > 1 || hasNewChanges) && (
            <SplitButtonContainer>
              <LeftButton
                kind="alternative"
                size="large"
                onClick={(e) => {
                  e.stopPropagation()
                  openLinkInNewWindow(latestPr.url)
                }}
              >
                <Horizontal alignItems="center" gap="8px">
                  <GithubFill className={Icon20} />
                  #{latestPr.number}
                  <PrStatus kind={latestPr.state}>{upperCase(latestPr.state)}</PrStatus>
                </Horizontal>
              </LeftButton>

              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <RightButton kind="alternative" size="large">
                    <ChevronDownLight
                      color="var(--secondary-text-color)"
                      className={cx(Icon16, dropdownOpen ? Rotate180 : Rotate0)}
                    />
                  </RightButton>
                </DropdownMenuTrigger>

                <StyledDropdownMenuContent align="end">
                  {hasNewChanges && (
                    <Vertical gap="8px">
                      <StyledDropdownMenuItem
                        onClick={() => {
                          props.model.gitHubModel.trackCreatePullRequestClick()
                          setPrDialogOpen(true)
                          setDropdownOpen(false)
                        }}
                      >
                        Create pull request
                      </StyledDropdownMenuItem>

                      <DropdownMenuSeparator />
                    </Vertical>
                  )}

                  <StyledDropdownMenuItem disabled>
                    <PrCounter>
                      {`${openedPrs.length} ${pluralize("pull request", openedPrs.length)}`}
                    </PrCounter>
                  </StyledDropdownMenuItem>

                  {openedPrs.map((pr) => (
                    <StyledDropdownMenuItem
                      key={pr.number}
                      onClick={() => {
                        openLinkInNewWindow(pr.url)
                        setDropdownOpen(false)
                      }}
                    >
                      <PrElement alignItems="center" gap="8px">
                        {versionTitle(pr.taskVersion)}
                        <GithubFill className={Icon20} />
                        #{pr.number}
                        <PrStatus kind={pr.state}>{upperCase(pr.state)}</PrStatus>
                      </PrElement>
                    </StyledDropdownMenuItem>
                  ))}
                </StyledDropdownMenuContent>
              </DropdownMenu>
            </SplitButtonContainer>
          )}
        </>
      )}

      {task && me && (
        <CreatePrDialog
          isOpen={prDialogOpen}
          onOpenChange={setPrDialogOpen}
          taskModel={props.model}
          taskLink={props.model.taskLink}
          task={task}
          author={me}
          historyItems={props.model.historyItems.get()}
        />
      )}
    </>
  )
})

const StyledDropdownMenuContent = styled(DropdownMenuContent)`
  min-width: 120px;
`

const StyledDropdownMenuItem = styled(DropdownMenuItem)<{ disabled?: boolean }>`
  opacity: ${props => (props.disabled ?? false ? 0.5 : 1)};
`

const PrCounter = styled(Horizontal)`
  color: var(--secondary-text-color);
`

const PrElement = styled(Horizontal)`
  padding: 4px 0;
`

const GitHubButton = styled(Button)`
  gap: 8px;

  padding: 8px 10px;

  font-weight: 400;
`

const LeftButton = styled(Button)`
`

const RightButton = styled(Button)`
  justify-content: center;

  padding-right: 12px;
  padding-left: 8px;
`
